import { differenceInDays, parse } from "date-fns";
import { useEffect } from "react";

import { DATE_FORMATS_FNS } from "components";
import { useTypedQuery } from "hooks/useTypedQuery";
import {
  getPaginatedHistoryInvoices,
  setPaginatedHistoryInvoices,
  usePaginatedHistoryInvoices
} from "modules/ExactMonitoring/components/Billing/components/InvoiceHistoryModal/hooks";
import { ExactFailuresQueryKeys } from "modules/ExactMonitoring/queryKeys";
import { ApiInstance } from "utils/Api";

import type { PaginatedInvoice } from "modules/ExactMonitoring/queryKeys";
import type { HistoricalInvoice, PaginatedHistoricalInvoices } from "modules/LocationLicenseOverview/queryKeys";

type UseInvoicesProps = {
  dealerId: number;
  dealerLocationId: number;
  paginationUrl?: string;
  currentInvoicePage?: number;
};

const getSortedResults = (data: HistoricalInvoice[]) =>
  [...data].sort((a, b) =>
    differenceInDays(parse(b.Modified, DATE_FORMATS_FNS.dateMonthYearSlash, new Date()), parse(a.Modified, DATE_FORMATS_FNS.dateMonthYearSlash, new Date()))
  );

export const useInvoices = ({ paginationUrl = "", currentInvoicePage = 0, dealerId, dealerLocationId }: UseInvoicesProps) => {
  const paginatedInvoices = usePaginatedHistoryInvoices();

  const handleUpdatePaginatedInvoices = (data: PaginatedHistoricalInvoices) => {
    const activeId = dealerId || dealerLocationId;
    if (!activeId) return { data: [] };

    const existingIndex = paginatedInvoices.data.findIndex(({ id }) => id === activeId);

    let updatedInvoices: PaginatedInvoice[] = [...paginatedInvoices.data, { id: activeId, data: { [currentInvoicePage]: data } }];
    if (existingIndex !== -1) updatedInvoices = paginatedInvoices.data.with(existingIndex, { id: activeId, data: { [currentInvoicePage]: data } });

    return { data: updatedInvoices };
  };

  const query = useTypedQuery({
    queryKey: ExactFailuresQueryKeys.invoiceHistory,
    queryFn: async ({ endpoint, baseUrl }) => {
      const invoices = getPaginatedHistoryInvoices();

      if (invoices.data.length > 0) {
        const activeId = dealerId || dealerLocationId;

        if (activeId) {
          const existingData = paginatedInvoices.data.find(({ id }) => id === activeId);
          if (existingData?.data[currentInvoicePage]) return existingData.data[currentInvoicePage];
        }
      }

      const response = await ApiInstance.post(
        endpoint,
        {
          ...(dealerId && { dealer_id: dealerId }),
          ...(dealerLocationId && { dealer_location_id: dealerLocationId }),
          ...(paginationUrl && { pagination_url: paginationUrl }),
          page_index: currentInvoicePage
        },
        baseUrl
      );

      setPaginatedHistoryInvoices(handleUpdatePaginatedInvoices(response.data));
      return { __next: response.data.__next, results: getSortedResults(response.data.results) };
    },
    enabled: false
  });

  useEffect(() => {
    if (dealerId || dealerLocationId) query.refetch();
  }, [dealerId, dealerLocationId]);

  return { paginatedInvoices, query };
};
